import { Box, Chip, TextField, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface SalesforgeIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    apiKey?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: { apiKey: string }) => void;
  getToken: () => Promise<string>;
}

export default function SalesforgeIntegrationForm({
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
  getToken,
}: SalesforgeIntegrationFormProps) {
  const [apiKey, setApiKey] = useState(initialData?.apiKey || "");
  const [validating, setValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState<{
    isValid: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    onValidationChange(apiKey.trim() !== "");
  }, [apiKey, onValidationChange]);

  useEffect(() => {
    onDataChange({ apiKey });
  }, [apiKey, onDataChange]);

  const validateCredentials = async () => {
    if (!apiKey.trim()) {
      setValidationStatus({
        isValid: false,
        message: "Please enter an API key",
      });
      return;
    }

    setValidating(true);
    setValidationStatus(null);

    try {
      const currentToken = await getToken();
      await axios.post(
        `${hostname}/integrations/salesforge/validate`,
        { apiKey },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      setValidationStatus({ isValid: true, message: "API key is valid." });
    } catch (error: any) {
      setValidationStatus({ isValid: false, message: parseError(error) });
    } finally {
      setValidating(false);
    }
  };

  const getValidationChip = () => {
    if (!validationStatus) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Chip
          label={`${validationStatus.isValid ? "✓" : "✗"} ${
            validationStatus.message
          }`}
          color={validationStatus.isValid ? "primary" : "error"}
          variant="outlined"
          size="small"
        />
      </Box>
    );
  };

  return (
    <Box>
      <TextField
        sx={{ mt: 1 }}
        fullWidth
        type="password"
        value={apiKey}
        label="Salesforge API Key"
        helperText="Your Salesforge API key from Settings → Integrations"
        onChange={(e) => {
          setApiKey(e.target.value);
          setValidationStatus(null);
        }}
        required
      />

      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <LoadingButton
          variant="outlined"
          size="small"
          onClick={validateCredentials}
          loading={validating}
          disabled={!apiKey.trim()}
        >
          Validate Credentials
        </LoadingButton>
      </Box>

      {getValidationChip()}

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          To get your Salesforge API key:
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          component="ol"
          sx={{ pl: 2 }}
        >
          <li>Go to your Salesforge dashboard</li>
          <li>Navigate to Settings → Integrations</li>
          <li>Generate or copy your API key</li>
          <li>Paste it above and click "Validate Credentials"</li>
        </Typography>
      </Box>
    </Box>
  );
}
